export const en = {
  common: {
    loading: 'Loading…',
    error: 'Loading error',
    retry: 'Retry',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    confirm: 'Confirm',
    close: 'Close',
    search: 'Search',
    seeAll: 'See all →',
    noData: 'No data',
    seeAllResults: 'See all results',
    noTitle: 'Untitled',
    unknown: 'Unknown',
    // Relative time
    justNow: 'Just now',
    ago: 'ago',
    min: 'min',
    hour: 'h',
    day: 'd',
    // Relative time with interpolation (functions)
    relativeMin: (n) => `${n} min ago`,
    relativeHour: (n) => `${n}h ago`,
    relativeDay: (n) => `${n}d ago`,
    // Notifications header
    notifAgo: (n) => `${n} min ago`,
    notifHoursAgo: (n) => `${n}h ago`,
    notifDaysAgo: (n) => `${Math.floor(n)}d ago`,
    // Future relative time (for Reminders)
    inMoment: 'in a moment',
    inMinutes: (n) => `in ${n} min`,
    inHours: (n) => `in ${n}h`,
    inDays: (n) => `in ${n} day${n > 1 ? 's' : ''}`,
  },

  sidebar: {
    ariaLabel: 'Main menu',
    close: 'Close menu',
    open: 'Open menu',
    collapse: 'Collapse menu',
    expand: 'Expand menu',
    upgradePro: 'Upgrade to Pro',
    logout: 'Log out',
    defaultUser: 'User',
    menu: {
      dashboard: { name: 'Dashboard', description: 'Overview' },
      agents: { name: 'AI Agents', description: 'Manage agents' },
      produits: { name: 'Products', description: 'Catalog' },
      whatsapp: { name: 'WhatsApp', description: 'WhatsApp messages' },
      baseConnaissance: { name: 'Knowledge Base', description: 'Documentation' },
      ragChat: { name: 'AI Assistant (RAG)', description: 'Chat on your documents' },
      ragDocs: { name: 'AI Documents', description: 'Gemini RAG base' },
      commandes: { name: 'Orders', description: 'Order management' },
      caisse: { name: 'Cash Register', description: 'Sales & collections' },
      factures: { name: 'Invoices', description: 'Quotes & payments' },
      relances: { name: 'Follow-ups', description: 'Scheduled follow-ups' },
      calendrier: { name: 'Calendar', description: 'Schedule' },
      parametres: { name: 'Settings', description: 'Configuration' },
    },
  },

  header: {
    greetingMorning: 'Good morning',
    greetingAfternoon: 'Good afternoon',
    greetingEvening: 'Good evening',
    subtitle: "Here's what happened today on Swift AI",
    searchPlaceholder: 'Search contacts, messages...',
    searching: 'Searching...',
    noResults: (q) => `No results for "${q}"`,
    searchError: 'Search error',
    notifications: 'Notifications',
    markAllRead: 'Mark all as read',
    noNotifications: 'No notifications',
    settings: 'Settings',
    defaultUser: 'User',
  },

  dashboard: {
    title: 'Dashboard',
    loadingText: 'Loading dashboard...',
    stats: {
      activeConversations: 'Active conversations',
      today: 'Today',
      pendingReminders: 'Pending follow-ups',
      inProgress: 'In progress',
      responseRate: 'Response rate',
      thisMonth: 'This month',
      avgResponseTime: 'Average response time',
      last7Days: 'Last 7 days',
      noConversation: 'No conversations',
      totalConversations: (n) => `out of ${n} conversation${n > 1 ? 's' : ''} total`,
      noReminder: 'No active follow-ups',
      remindersToProcess: 'Follow-ups to process',
      noData: 'No data yet',
      resolved: (r, t) => `${r} resolved / ${t} total`,
      avgResponseDetail: 'Avg. delay before first reply',
    },
    charts: {
      weeklyActivity: 'Weekly activity',
      conversationsAndReminders: 'Conversations and follow-ups',
      conversations: 'Conversations',
      reminders: 'Follow-ups',
      activity24h: '24h activity',
      hourlyVolume: 'Activity volume per hour',
      realtime: 'Real-time',
      peak: 'Peak',
      average: 'Average',
      activeHours: 'Active hours',
    },
    recentConversations: 'Recent conversations',
    scheduledReminders: 'Scheduled follow-ups',
    noRecentConversations: 'No recent conversations',
    noScheduledReminders: 'No scheduled follow-ups',
    unknownClient: 'Unknown client',
    noMessage: 'No message',
    statusWaiting: 'Pending',
    statusDone: 'Done',
  },

  settings: {
    title: 'Settings',
    subtitle: 'Manage your account and preferences',
    logout: 'Log out',
    tabs: {
      profile: 'Profile',
      subscription: 'Subscription',
      preferences: 'Preferences',
      notifications: 'Notifications',
      security: 'Security',
      smsGateway: 'SMS Gateway',
      appearance: 'Appearance',
      data: 'Data',
      help: 'Help',
    },
    profile: {
      title: 'Profile information',
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email',
      emailNote: '(cannot be changed)',
      saved: 'Profile updated successfully!',
      error: 'Error updating profile',
      avatarSuccess: 'Avatar updated successfully!',
      avatarError: 'Error uploading avatar',
    },
    subscription: {
      creditsTitle: 'Available AI credits',
      currentBalance: 'Current balance',
      aiCredits: 'AI credits',
      billingTitle: 'Subscription & billing',
      comingSoon: 'Coming soon',
      comingSoonText: 'Subscription management will be available soon from your account. Check out our pricing page in the meantime.',
      seePricing: 'See pricing →',
    },
    preferences: {
      title: 'General preferences',
      language: 'App language',
      timezone: 'Timezone',
      dateFormat: 'Date format',
      langSuccess: 'Language updated successfully!',
      langError: 'Error updating language',
    },
    notifications: {
      title: 'Notification preferences',
      email: 'Email',
      emailDesc: 'Receive notifications by email',
      push: 'Push',
      pushDesc: 'Push notifications on mobile',
      sms: 'SMS',
      smsDesc: 'Important alerts by SMS',
      desktop: 'Desktop',
      desktopDesc: 'Desktop notifications',
      updateSuccess: 'Notification preferences updated!',
      updateError: 'Error updating notifications',
    },
    security: {
      title: 'Account security',
      passwordTitle: 'Password',
      changePassword: 'Change password',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmPassword: 'Confirm password',
      currentPasswordPlaceholder: 'Enter your current password',
      newPasswordPlaceholder: 'Enter the new password',
      confirmPasswordPlaceholder: 'Confirm the new password',
      update: 'Update',
      passwordMismatch: 'Passwords do not match',
      passwordSuccess: 'Password changed successfully!',
      passwordError: 'Error changing password',
      twoFATitle: 'Two-factor authentication',
      twoFAEnabled: '2FA enabled',
      twoFADisabled: '2FA not enabled',
      twoFAProtected: 'Your account is protected by 2FA',
      twoFAAdd: 'Add an extra layer of security',
      enable: 'Enable',
      disable: 'Disable',
      twoFADisabledSuccess: '2FA disabled successfully!',
      twoFAError: 'Error managing two-factor authentication',
      sessionsTitle: 'Active sessions',
      disconnect: 'Disconnect',
      currentSession: 'Current session',
      sessionRevokeSuccess: 'Session revoked successfully!',
      sessionRevokeError: 'Error revoking session',
    },
    smsGateway: {
      title: 'SMS Mobile Money Gateway',
      description: 'Configure a secret token on your Android phone so MTN MoMo and Orange Money SMS are automatically intercepted and recorded in your cash register.',
      tokenActive: 'Active token',
      noToken: 'No token configured',
      revoke: 'Revoke',
      generate: 'Generate a token',
      regenerate: 'Regenerate token',
      tokenNote: 'Token — copy it now, it will not be shown again',
      webhookUrl: 'Webhook URL (paste into the Android app)',
      generateSuccess: 'Token generated — copy it now, it will not be shown again.',
      generateError: 'Error generating token.',
      revokeSuccess: 'Token revoked.',
      revokeError: 'Error revoking token.',
      revokeConfirm: 'Revoke the token? The Android app will return 401 until reconfigured.',
      regenerateConfirm: 'Regenerating will revoke the old token. The Android app will need to be reconfigured. Continue?',
      androidTitle: 'Android app configuration',
      steps: [
        { t: 'Install SMS Gateway on your Android phone (the one that receives MoMo SMS).', sub: 'Recommended app: "SMS to URL Forwarder" or "SMS Gateway"' },
        { t: 'In the app, set the Webhook URL above as the destination URL.', sub: 'Method: POST' },
        { t: 'Add the custom header:', sub: 'X-Gateway-Token: <your-token>' },
        { t: 'Configure the body as JSON with the fields:', sub: '{ "from": "{from}", "message": "{message}" }' },
        { t: 'Enable forwarding for all incoming SMS.', sub: 'MTN MoMo and Orange Money SMS will be automatically intercepted.' },
      ],
    },
    appearance: {
      title: 'Appearance',
      theme: 'Theme',
      dark: 'Dark',
      light: 'Light',
      auto: 'Auto',
      darkSuccess: 'Dark theme enabled!',
      lightSuccess: 'Light theme enabled!',
      autoSuccess: 'Auto theme enabled!',
      fontSize: 'Font size',
      small: 'Small',
      medium: 'Medium',
      large: 'Large',
    },
    data: {
      title: 'Data management',
      exportTitle: 'Export your data',
      exportDesc: 'Download all your data in JSON format',
      export: 'Export data',
      exportSuccess: 'Data exported successfully!',
      exportError: 'Error exporting data',
      deleteTitle: 'Delete account',
      deleteWarning: 'Warning: this action is irreversible',
      deleteDesc: 'Deleting your account will permanently delete:',
      deleteItems: [
        'All your conversations and AI agents',
        'Your personal data and preferences',
        'Your usage history',
        'All remaining credits',
      ],
      deleteBtn: 'Delete my account',
      deleteConfirm1: 'Are you sure you want to delete your account? This action is irreversible.',
      deleteConfirm2: 'This action will permanently delete all your data. Confirm?',
      deleteError: 'Error deleting account',
    },
    help: {
      title: 'Help center',
      faq: [
        { q: 'How to create an agent?', a: 'Step-by-step guide to create your first AI agent' },
        { q: 'Set up WhatsApp', a: 'Connect your WhatsApp Business account' },
        { q: 'Credit management', a: 'Understand and manage your credit usage' },
      ],
      contactSupport: 'Contact support',
      visitDocs: 'Visit documentation',
    },
  },

  agents: {
    loading: 'Loading agents...',
    loadError: 'Error loading agents',
    deleteError: 'Error deleting agent',
    title: 'AI Agents',
    subtitle: 'Manage your intelligent assistants',
    create: 'Create an agent',
    total: 'Total agents',
    active: 'Active',
    noAgents: 'No agents created',
    noAgentsDesc: 'Create your first AI agent to automate your WhatsApp conversations',
    createFirst: 'Create my first agent',
    deleteConfirmTitle: 'Delete this agent?',
    deleteConfirmDesc: (name) => `The agent "${name}" will be permanently deleted.`,
    deleteConfirmBtn: 'Delete',
    statusActive: 'Active',
    statusPaused: 'Paused',
    conversations: 'conversations',
    configure: 'Configure',
    details: 'Details',
  },

  produits: {
    loading: 'Loading products...',
    loadError: 'Error loading products',
    deleteError: 'Error deleting product',
    title: 'Products & Services',
    subtitle: 'Manage your catalog',
    catalogue: 'Catalog',
    add: 'Add a product',
    addBtn: 'Add',
    search: 'Search a product...',
    allCategories: 'All categories',
    allCat: 'All',
    totalProducts: 'Total products',
    catalogValue: 'Catalog value',
    totalValue: 'Total value',
    categories: 'Categories',
    available: 'Available',
    sortedByPopularity: 'Sorted by popularity',
    productCount: (n) => `${n} product${n !== 1 ? 's' : ''}`,
    unlimited: 'Unlimited',
    outOfStockShort: 'Out of stock',
    inStockCount: (n) => `${n} in stock`,
    noProductsEmpty: 'No products',
    noProducts: 'No products found',
    noProductsDesc: 'Start by adding your first products and services to the catalog',
    addFirst: 'Add a product',
    deleteConfirmTitle: 'Delete this product?',
    deleteConfirmDesc: (name) => `The product "${name}" will be permanently deleted.`,
    deleteConfirmBtn: 'Delete',
    irreversible: 'This action is irreversible',
    willDelete: (name) => `You are about to delete ${name}.`,
    viewModal: 'View product',
    editBtn: 'Edit',
    deleteBtn: 'Delete',
    product: 'Product',
    price: 'Price',
    stock: 'Stock',
    category: 'Category',
    actions: 'Actions',
    inStock: 'In stock',
    outOfStock: 'Out of stock',
    currency: (price) => `${price.toLocaleString('en-US')} XOF`,
  },

  commandes: {
    title: 'Orders',
    subtitle: 'Order management',
    realtime: 'Real-time sales tracking',
    search: 'Search...',
    searchPlaceholder: 'Search by client, product or ID...',
    all: 'All',
    completed: 'Completed',
    inProgress: 'In progress',
    pending: 'Pending',
    totalOrders: 'Total Orders',
    totalRevenue: 'Total Revenue',
    completionRate: 'Completion Rate',
    waitingOrders: 'Pending',
    allOrders: 'all orders',
    needsAttention: 'need attention',
    completed_n: (n) => `${n} completed`,
    thisMonth: (n) => `+${n} this month`,
    noOrders: 'No orders found',
    realtimeUpdate: 'Real-time update',
    orderCount: (n) => `${n} order${n !== 1 ? 's' : ''}`,
    tableClient: 'Client',
    tableProduct: 'Product',
    tableAmount: 'Amount',
    tableStatus: 'Status',
    tableDate: 'Date',
  },

  factures: {
    title: 'Invoices',
    subtitle: 'Quotes & payments',
    subtitleAI: 'AI drafts and manual invoices',
    loading: 'Loading invoices...',
    create: 'New invoice',
    newInvoice: 'New invoice',
    template: 'Template',
    search: 'Search an invoice...',
    searchPlaceholder: 'Search by invoice # or client name...',
    all: 'All',
    filterAll: 'All',
    filterDraft: 'Drafts',
    filterSent: 'Sent',
    filterPaid: 'Paid',
    filterOverdue: 'Overdue',
    filterCancelled: 'Cancelled',
    draft: 'Draft',
    sent: 'Sent',
    paid: 'Paid',
    overdue: 'Overdue',
    cancelled: 'Cancelled',
    pending: 'Pending',
    totalLabel: 'Total Invoices',
    collected: 'Collected',
    draftsCount: 'Drafts',
    toCollect: 'Outstanding',
    total: 'Total invoices',
    totalAmount: 'Total amount',
    paidAmount: 'Collected amount',
    invoiceCount: (n) => `${n} invoice${n !== 1 ? 's' : ''}`,
    noInvoices: 'No invoices',
    noResults: 'No results',
    adjustFilters: 'Try adjusting your filters',
    createFirst: 'Create your first invoice or generate one from an order',
    tableInvoiceNum: 'Invoice #',
    tableClient: 'Client',
    tableOrigin: 'Origin',
    tableAmount: 'Amount',
    tableStatus: 'Status',
    tableDueDate: 'Due date',
    tableActions: 'Actions',
    dueDate: 'Due date',
    aiAgent: 'AI Agent',
    manual: 'Manual',
    tooltipViewConv: 'View source conversation',
    tooltipEdit: 'Edit draft',
    tooltipSend: 'Send (generates PDF + payment link)',
    tooltipViewPdf: 'View PDF',
    tooltipWhatsApp: 'Send via WhatsApp',
    tooltipMarkPaid: 'Mark as paid',
    tooltipCancel: 'Cancel invoice',
  },

  relances: {
    title: 'Follow-ups',
    titleFull: 'Scheduled follow-ups',
    subtitle: 'Scheduled follow-ups',
    subtitleFull: 'Smart follow-ups scheduled by AI',
    loading: 'Loading follow-ups...',
    create: 'Schedule a follow-up',
    newRelance: 'New follow-up',
    noRelances: 'No scheduled follow-ups',
    noRelancesDesc: 'The agent will create them automatically when a client asks to be contacted later. You can also schedule one manually.',
    pending: 'Pending',
    sent: 'Sent',
    cancelled: 'Cancelled',
    failed: 'Failed',
    filterAll: 'All',
    filterSent: 'Sent',
    filterCancelled: 'Cancelled',
    filterFailed: 'Failed',
    statTotal: 'Total',
    statSent: 'Sent',
    statCancelled: 'Cancelled',
    searchPlaceholder: 'Search by client or reason...',
    from: 'From',
    to: 'to',
    clearDates: 'Clear dates',
    tableClient: 'Client',
    tableWhen: 'When',
    tableReason: 'Reason',
    tableSource: 'Source',
    tableStatus: 'Status',
    tableActions: 'Actions',
    overdueLabel: 'overdue',
    hideContext: 'Hide context',
    viewContext: 'View context',
    hideDetails: 'Hide details',
    viewDetails: 'View details',
    ai: 'AI',
    manual: 'Manual',
    cancelConfirm: (name) => `Cancel follow-up for ${name}?`,
    deleteConfirm: 'Permanently delete this follow-up?',
    cancelSuccess: 'Follow-up cancelled',
    cancelError: 'Unable to cancel follow-up',
    deleteSuccess: 'Follow-up deleted',
    deleteError2: 'Unable to delete follow-up',
    createSuccess: 'Follow-up scheduled',
    editSuccess: 'Follow-up rescheduled',
    opError: 'Operation failed',
    toastCreated: (client, date) => `🤖 The agent scheduled a follow-up for ${client} on ${date}`,
    toastCreatedTitle: 'New follow-up',
    toastSent: (client) => `Follow-up sent to ${client}`,
    toastSentTitle: 'Follow-up sent',
    errorTitle: 'Error',
    contextLabel: 'Context',
    sentMessage: 'Sent message',
    attempts: (n) => `Send attempts: ${n}`,
    noDetails: 'No additional details.',
    msgUnavailable: '— (message not available)',
    noConv: 'No conversations available.',
    selectConv: '— Select a conversation —',
    convLabel: 'Conversation *',
    dateLabel: 'Date and time *',
    reasonLabel: 'Reason *',
    contextOptLabel: 'Context',
    optional: 'optional',
    contextPlaceholder: 'Summary of the situation to help the agent draft the message…',
    reasonPlaceholder: 'e.g. Client wants to think before ordering',
    errNoConv: 'Please select a conversation.',
    errNoDate: 'Please choose a date and time.',
    errFutureDate: 'The follow-up date must be in the future.',
    errNoReason: 'Please provide a reason.',
    reschedule: 'Reschedule follow-up',
    scheduleNew: 'New follow-up',
    scheduleFor: 'Schedule a follow-up for a client',
    rescheduleBtn: 'Reschedule',
    scheduleBtn: 'Schedule',
    tooltipReschedule: 'Reschedule',
    tooltipCancel: 'Cancel',
    tooltipDelete: 'Delete',
    tooltipDetails: 'Details',
    client: 'Client',
    reason: 'Reason',
    scheduledAt: 'Scheduled at',
  },

  credits: {
    title: 'Credits history',
    balance: 'Current balance',
    loading: 'Loading...',
    noHistory: 'No transactions',
    refill: 'Top up',
    spent: 'Spent',
    added: 'Added',
    date: 'Date',
    type: 'Type',
    amount: 'Amount',
    description: 'Description',
  },

};
